import React, { useEffect } from 'react';
import { 
  Box, 
  Typography, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon, 
  Badge, 
  useMediaQuery, 
  useTheme,
  Chip
} from '@mui/material';
import { 
  Mail, 
  MailOutline, 
  PriorityHigh
} from '@mui/icons-material';
import { DashboardWidget } from '../../store/slices/dashboardWidgetsSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchMessages, fetchUnreadCount, markAsRead, InternalMessage } from '../../store/slices/internalMessagesSlice';
import { addNotification } from '../../store/slices/uiSlice';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

interface MessagesWidgetProps {
  widget: DashboardWidget;
  onMessageClick?: (message: InternalMessage) => void;
  noWrapper?: boolean;
}

const MessagesWidget: React.FC<MessagesWidgetProps> = ({ widget, onMessageClick, noWrapper = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useAppDispatch();
  const { inbox, unreadCount, loading } = useAppSelector((state) => state.internalMessages);
  const { user } = useAppSelector((state) => state.auth);

  useEffect(() => {
    let previousUnreadCount = 0;
    
    const loadMessages = async () => {
      // Lade alle Nachrichten (keine Limitierung nach Alter)
      await dispatch(fetchMessages({ type: 'inbox', limit: 50 }));
      const result = await dispatch(fetchUnreadCount());
      const currentUnreadCount = result.payload as number;
      
      // Benachrichtigung bei neuen Nachrichten
      if (currentUnreadCount > previousUnreadCount && previousUnreadCount > 0) {
        dispatch(addNotification({
          type: 'info',
          title: 'Neue Nachricht',
          message: `Sie haben ${currentUnreadCount - previousUnreadCount} neue Nachricht(en) erhalten.`
        }));
      }
      
      previousUnreadCount = currentUnreadCount;
    };
    
    // Beim ersten Laden
    loadMessages();
    
    // Polling für neue Nachrichten alle 30 Sekunden
    const interval = setInterval(() => {
      loadMessages();
    }, 30000);

    return () => clearInterval(interval);
  }, [dispatch]);

  const handleMessageClick = async (message: InternalMessage) => {
    // Markiere die Nachricht als gelesen, wenn sie ungelesen ist und der aktuelle Benutzer der Empfänger ist
    if ((message.status === 'sent' || message.status === 'delivered') && user) {
      const recipientId = typeof message.recipientId === 'object' ? message.recipientId._id : message.recipientId;
      if (recipientId === user._id) {
        console.log('🔔 MessagesWidget: Markiere Nachricht als gelesen:', message._id, 'Aktueller unreadCount:', unreadCount);
        // Redux reduziert den Count sofort, aber wir aktualisieren auch vom Backend für Konsistenz
        await dispatch(markAsRead(message._id));
        console.log('✅ MessagesWidget: Nachricht markiert, neuer unreadCount sollte reduziert sein');
        // Aktualisiere unreadCount vom Backend nach kurzer Verzögerung (für Konsistenz)
        setTimeout(async () => {
          await dispatch(fetchUnreadCount());
        }, 500);
      }
    }
    
    if (onMessageClick) {
      onMessageClick(message);
    }
  };


  const listScrollbarSx = noWrapper
    ? {
        flexGrow: 1,
        minHeight: 0,
        overflowY: 'auto' as const,
        scrollbarWidth: 'thin' as const,
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-track': {
          bgcolor: theme.palette.mode === 'dark' ? 'action.hover' : 'divider',
          borderRadius: 1,
        },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: theme.palette.mode === 'dark' ? 'action.selected' : 'action.hover',
          borderRadius: 1,
        },
      }
    : undefined;

  return (
    <Box sx={{ 
      minHeight: noWrapper ? 0 : '100%',
      height: noWrapper ? '100%' : 'auto',
      width: noWrapper ? '100%' : undefined,
      display: 'flex', 
      flexDirection: 'column', 
      p: noWrapper ? 0 : { xs: 1.5, sm: 2 },
      flex: noWrapper ? 1 : undefined,
      overflow: noWrapper ? 'hidden' : undefined,
      bgcolor: noWrapper ? 'transparent' : undefined,
    }}>
      {!noWrapper && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 1, sm: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Badge badgeContent={unreadCount} color="error">
              <Mail color="primary" />
            </Badge>
            <Typography 
              variant={isMobile ? 'subtitle1' : 'h6'} 
              sx={{ fontWeight: 600 }}
            >
              {widget.title}
            </Typography>
          </Box>
          {unreadCount > 0 && (
            <Chip 
              label={`${unreadCount} ungelesen`} 
              size="small" 
              color="error"
              sx={{ ml: 1 }}
            />
          )}
        </Box>
      )}
      {noWrapper && unreadCount > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
          <Chip label={`${unreadCount} ungelesen`} size="small" color="error" />
        </Box>
      )}
      <Box sx={{ 
        flex: inbox.length === 0 ? 'none' : 1,
        overflow: noWrapper ? 'hidden' : inbox.length > 4 ? 'auto' : 'visible',
        minHeight: inbox.length === 0 ? 'auto' : noWrapper ? 0 : 'none',
        ...(noWrapper && { display: 'flex', flexDirection: 'column' }),
      }}>
        {loading ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            Lade Nachrichten...
          </Typography>
        ) : inbox.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            Keine Nachrichten
          </Typography>
        ) : (
          <List sx={{ py: 0, ...listScrollbarSx }}>
            {/* Sortiere Nachrichten nach Datum: neueste zuerst */}
            {[...inbox].sort((a, b) => {
              const dateA = new Date(a.createdAt).getTime();
              const dateB = new Date(b.createdAt).getTime();
              return dateB - dateA; // Neueste zuerst
            }).map((message: InternalMessage, index: number, sortedArray) => {
              const isUnread = message.status === 'sent' || message.status === 'delivered';
              
              // Extrahiere patientId aus der Nachricht (kann ObjectId-Objekt oder String sein)
              let patientId: string | null = null;
              if (message.patientId) {
                // Wenn patientId ein Objekt ist (z.B. ObjectId), extrahiere _id oder toString()
                if (typeof message.patientId === 'object' && message.patientId !== null) {
                  patientId = (message.patientId as any)._id ? String((message.patientId as any)._id) : String(message.patientId);
                } else {
                  patientId = String(message.patientId);
                }
              }
              
              console.log('🔍 MessagesWidget: Processing message', { 
                messageId: message._id, 
                subject: message.subject,
                patientId: patientId,
                patientIdType: typeof patientId,
                fullMessage: message 
              });
              
              return (
                <ListItem 
                  key={message._id} 
                  divider={index < sortedArray.length - 1}
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('🔍 MessagesWidget: Item clicked', { 
                      messageId: message._id, 
                      patientId: patientId,
                      hasPatientId: !!patientId,
                      fullMessage: message 
                    });
                    
                    // Wenn ein onMessageClick-Handler vorhanden ist, verwende ihn
                    if (onMessageClick) {
                      handleMessageClick(message);
                      onMessageClick(message);
                    } else {
                      // Fallback: Wenn patientId vorhanden ist, navigiere zum Patienten
                      if (patientId) {
                        const patientIdStr = typeof patientId === 'string' ? patientId : String(patientId);
                        console.log('🔍 MessagesWidget: Navigating to patient labor values', { 
                          patientId: patientIdStr, 
                          originalPatientId: patientId, 
                          fullMessage: message 
                        });
                        window.location.href = `/patient-organizer/${patientIdStr}?tab=laborwerte`;
                      } else {
                        // Sonst zur Interne-Nachrichten-Seite navigieren
                        console.log('🔍 MessagesWidget: No patientId, navigating to internal messages page', { messageId: message._id });
                        window.location.href = '/internal-messages';
                      }
                    }
                  }}
                  sx={{ 
                    py: { xs: 1, sm: 1.5 },
                    px: { xs: 0.5, sm: 1 },
                    cursor: 'pointer',
                    bgcolor: isUnread ? 'action.selected' : 'transparent',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      borderRadius: 1
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: { xs: 32, sm: 40 } }}>
                    {isUnread ? (
                      <MailOutline color="primary" />
                    ) : (
                      <Mail color="action" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography 
                          variant={isMobile ? 'body2' : 'body1'} 
                          component="span"
                          sx={{ 
                            fontWeight: isUnread ? 600 : 400,
                            flex: 1,
                            minWidth: 0
                          }}
                          noWrap
                        >
                          {message.subject}
                        </Typography>
                        {message.priority === 'urgent' && (
                          <PriorityHigh color="error" fontSize="small" />
                        )}
                        {message.priority === 'high' && (
                          <Chip label="Wichtig" size="small" color="warning" sx={{ height: 20 }} />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography 
                          variant={isMobile ? 'caption' : 'body2'} 
                          color="text.secondary"
                          component="span"
                          sx={{ display: 'block' }}
                          noWrap
                        >
                          Von: {message.senderId.firstName} {message.senderId.lastName}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          component="span"
                          sx={{ display: 'block', mt: 0.5 }}
                        >
                          {format(new Date(message.createdAt), 'dd.MM.yyyy HH:mm', { locale: de })}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default MessagesWidget;

